import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";

const OPERATORS = ["ENKASH", "EARTHY", "AERONPAY"];

// Each vendor gets a fixed identity color so a long rules table can be
// scanned by vendor at a glance, without reading every row's text.
const OPERATOR_STYLE = {
  ENKASH: { dot: "bg-teal-600", text: "text-teal-800", bg: "bg-teal-50", ring: "ring-teal-600/20" },
  EARTHY: { dot: "bg-amber-600", text: "text-amber-800", bg: "bg-amber-50", ring: "ring-amber-600/20" },
  AERONPAY: { dot: "bg-violet-600", text: "text-violet-800", bg: "bg-violet-50", ring: "ring-violet-600/20" },
};
const FALLBACK_STYLE = { dot: "bg-slate-400", text: "text-slate-700", bg: "bg-slate-50", ring: "ring-slate-400/20" };

const OperatorBadge = ({ operator }) => {
  const style = OPERATOR_STYLE[operator] || FALLBACK_STYLE;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${style.bg} ${style.text} ring-1 ${style.ring} px-2.5 py-1 text-xs font-semibold`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
      {operator}
    </span>
  );
};

const FieldLabel = ({ children }) => (
  <label className="block mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
    {children}
  </label>
);

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 " +
  "outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20";

const emptyForm = {
  operator: "",
  minAmount: "",
  maxAmount: "",
  percentage: "",
  priority: "",
  isActive: true,
  merchantId: "",
};

const CcbpRouting = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [errorMsg, setErrorMsg] = useState("");

  const { callApi: getAllRules } = useApi("/admin/ccbp-routing/rules", "GET");
  const { callApi: createRule } = useApi("/admin/ccbp-routing/rule", "POST");
  const { callApi: updateRule } = useApi("/admin/ccbp-routing/rule", "PUT");
  const { callApi: deleteRuleApi } = useApi("/admin/ccbp-routing/rule", "DELETE");

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await getAllRules();
      setRules(Array.isArray(res) ? res : []);
    } catch (err) {
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setEditMode(false);
    setEditingId(null);
    setFormData(emptyForm);
    setErrorMsg("");
    setShowModal(true);
  };

  const openEditModal = (rule) => {
    setEditMode(true);
    setEditingId(rule.id);
    setFormData({
      operator: rule.operator || "",
      minAmount: rule.minAmount ?? "",
      maxAmount: rule.maxAmount ?? "",
      percentage: rule.percentage ?? "",
      priority: rule.priority ?? "",
      isActive: rule.isActive ?? true,
      merchantId: rule.merchantId || "",
    });
    setErrorMsg("");
    setShowModal(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    operator: formData.operator,
    minAmount: formData.minAmount === "" ? null : Number(formData.minAmount),
    maxAmount: formData.maxAmount === "" ? null : Number(formData.maxAmount),
    percentage: formData.percentage === "" ? null : Number(formData.percentage),
    priority: formData.priority === "" ? null : Number(formData.priority),
    isActive: formData.isActive,
    merchantId: formData.merchantId || null,
  });

  const handleSubmit = async () => {
    if (!formData.operator) {
      setErrorMsg("Select an operator");
      return;
    }

    if (formData.percentage !== "") {
      const pct = Number(formData.percentage);
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        setErrorMsg("Percentage must be between 0 and 100");
        return;
      }
    }

    if (formData.minAmount !== "" && formData.maxAmount !== "") {
      if (Number(formData.minAmount) > Number(formData.maxAmount)) {
        setErrorMsg("Min amount cannot be greater than max amount");
        return;
      }
    }

    try {
      const payload = buildPayload();

      const response = editMode
        ? await updateRule(payload, { id: editingId })
        : await createRule(payload);

      // Backend errors come back as HTTP 202 with an error_code/message body
      // (not a 4xx), so axios treats the call as "successful" — check the
      // payload shape ourselves instead of relying on a thrown exception.
      if (response?.error_code != null) {
        setErrorMsg(response.message || "Something went wrong");
        return;
      }

      setShowModal(false);
      setErrorMsg("");
      fetchRules();
    } catch (err) {
      setErrorMsg(err?.message || "Something went wrong");
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete routing rule for ${rule.operator}?`)) return;

    try {
      await deleteRuleApi(null, { id: rule.id });
      fetchRules();
    } catch (err) {
      alert(err?.message || "Failed to delete rule");
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      const response = await updateRule(
        {
          operator: rule.operator,
          minAmount: rule.minAmount,
          maxAmount: rule.maxAmount,
          percentage: rule.percentage,
          priority: rule.priority,
          isActive: !rule.isActive,
          merchantId: rule.merchantId,
        },
        { id: rule.id }
      );

      if (response?.error_code != null) {
        alert(response.message || "Failed to update rule");
        return;
      }

      fetchRules();
    } catch (err) {
      alert(err?.message || "Failed to update rule");
    }
  };

  return (
    <div className="p-6">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-slate-900">CCBP Routing Rules</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Decide which vendor handles a credit-card bill payment, by amount.
          </p>
        </div>

        <button
          onClick={openAddModal}
          className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800"
        >
          <span className="text-base leading-none">+</span> Add Rule
        </button>
      </div>

      {/* TABLE */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Operator
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Range
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Split
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Priority
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Status
              </th>
              <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-5 py-10 text-center text-sm text-slate-400">
                  Loading routing rules...
                </td>
              </tr>
            ) : rules.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-5 py-10 text-center">
                  <p className="text-sm font-medium text-slate-600">No CCBP routing rules yet</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Add one to start routing bill payments by amount.
                  </p>
                </td>
              </tr>
            ) : (
              rules.map((rule) => (
                <tr key={rule.id} className="transition hover:bg-slate-50">
                  <td className="px-5 py-3.5">
                    <OperatorBadge operator={rule.operator} />
                  </td>

                  <td className="px-5 py-3.5 text-slate-700 tabular-nums">
                    {rule.minAmount != null && rule.maxAmount != null
                      ? `₹${rule.minAmount} – ₹${rule.maxAmount}`
                      : "—"}
                  </td>

                  <td className="px-5 py-3.5 text-slate-700 tabular-nums">
                    {rule.percentage != null ? `${rule.percentage}%` : "—"}
                  </td>

                  <td className="px-5 py-3.5 text-slate-500">
                    {rule.priority != null ? `P${rule.priority}` : "—"}
                  </td>

                  <td className="px-5 py-3.5">
                    <button
                      onClick={() => handleToggleActive(rule)}
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                        rule.isActive
                          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 hover:bg-emerald-100"
                          : "bg-red-50 text-red-700 ring-1 ring-red-600/20 hover:bg-red-100"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          rule.isActive ? "bg-emerald-500" : "bg-red-500"
                        }`}
                      />
                      {rule.isActive ? "Active" : "Inactive"}
                    </button>
                  </td>

                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-4 text-sm font-medium">
                      <button
                        onClick={() => openEditModal(rule)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rule)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900">
              {editMode ? "Edit CCBP Rule" : "Add CCBP Rule"}
            </h3>
            <p className="text-sm text-slate-500 mt-1 mb-5">
              Set which vendor handles payments in this amount range.
            </p>

            <div className="space-y-4">
              <div>
                <FieldLabel>Operator</FieldLabel>
                <select
                  value={formData.operator}
                  onChange={(e) => handleChange("operator", e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select operator</option>
                  {OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Min amount</FieldLabel>
                  <input
                    value={formData.minAmount}
                    onChange={(e) => handleChange("minAmount", e.target.value)}
                    placeholder="e.g. 0"
                    inputMode="numeric"
                    className={inputClass}
                  />
                </div>
                <div>
                  <FieldLabel>Max amount</FieldLabel>
                  <input
                    value={formData.maxAmount}
                    onChange={(e) => handleChange("maxAmount", e.target.value)}
                    placeholder="e.g. 50000"
                    inputMode="numeric"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FieldLabel>Percentage</FieldLabel>
                  <input
                    value={formData.percentage}
                    onChange={(e) => handleChange("percentage", e.target.value)}
                    placeholder="0–100"
                    inputMode="numeric"
                    className={inputClass}
                  />
                </div>
                <div>
                  <FieldLabel>
                    Priority <span className="normal-case text-slate-400">(optional)</span>
                  </FieldLabel>
                  <input
                    value={formData.priority}
                    onChange={(e) => handleChange("priority", e.target.value)}
                    placeholder="e.g. 1"
                    inputMode="numeric"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                <span className="text-sm font-semibold text-slate-700">Status</span>
                <button
                  type="button"
                  onClick={() => handleChange("isActive", !formData.isActive)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                    formData.isActive
                      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20 hover:bg-emerald-100"
                      : "bg-red-50 text-red-700 ring-1 ring-red-600/20 hover:bg-red-100"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      formData.isActive ? "bg-emerald-500" : "bg-red-500"
                    }`}
                  />
                  {formData.isActive ? "Active" : "Inactive"}
                </button>
              </div>
            </div>

            {errorMsg && (
              <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-600/20">
                {errorMsg}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800"
              >
                Save rule
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CcbpRouting;
